/**
 * Collaborative locking system Socket event enumerations and type definitions
 * Four modules based on product logic design: Doctor Note & Diagnosis, Letters/MCs, Nursing Note, Dispensing & Charting
 */

// ==================== Common Type Definitions ====================

export interface LockSnapshot {
  /** User ID of the person who locked the document */
  lockedBy: string | null;

  /** User ID (alternative field name for compatibility) */
  userId?: string;

  /** Username of the person who locked the document */
  userName?: string;

  /** Username (alternative field name for compatibility) */
  lockedByUsername?: string;

  /** Lock time */
  lockedAt?: Date | string;

  /** Lock expiration time */
  expiresAt?: Date | string;

  /** Collaborative module identifier */
  module: CollaborativeModule;

  /** Access ID for the document/section */
  accessId: string;

  /** Visit ID (for backward compatibility) */
  visitId?: string;
}

export interface DraftUpdatePayload {
  /** Doctor's consultation note content */
  consultantNote?: string;

  /** Nursing note content */
  nursingNote?: string;

  /** Patient disposition content */
  patientDisposition?: string | null;

  /** Whether patient education/discharge advice is required */
  patientEducationRequired?: boolean;

  /** Selected discharge advice records */
  visitDischargeAdvices?: unknown[];

  /** Visit diagnoses data - modern format */
  diagnoses?: DiagnosisItem[];

  /** Visit diagnoses data - legacy format (for backward compatibility) */
  visitDiagnoses?: Record<string, unknown>;

  /** Visit letters data */
  visitLetters?: Record<string, unknown>;

  // Note: Letters/MC section typically does not need payload, only send events
}

export interface DiagnosisItem {
  icd10DiagnosisCode?: string;
  snomedDiagnosisIdId?: string;
  remarks?: string;
}

export type CollaborativeModule =
  | "consultNoteDiagnosis"
  | "letterMcs"
  | "nursingNote"
  | "dispensing"
  | "diagnosis"
  | "patientDisposition"
  | "dischargeAdvice";

export interface CollaborativeState {
  isLocked: boolean;
  lockSnapshot: LockSnapshot | null;
  isConnected: boolean;
  lastDraftUpdate: Date | null;
  autoSaveInterval: number; // 30 seconds
}

// ==================== Doctor Note & Diagnosis Events ====================

export enum ConsultNoteDiagnosisClientEvents {
  JoinRoom = "consultNoteDiagnosis:joinRoom",
  // LeaveRoom removed - server no longer supports it
  RequestLock = "consultNoteDiagnosis:lock",
  KeepAlive = "consultNoteDiagnosis:keepalive",
  DraftUpdate = "consultNoteDiagnosis:draft:update",
  ReleaseLock = "consultNoteDiagnosis:unlock",
}

export enum ConsultNoteDiagnosisServerEvents {
  Snapshot = "consultNoteDiagnosis:lock:snapshot",
  LockAck = "consultNoteDiagnosis:lock:ack",
  LockError = "consultNoteDiagnosis:lock:error",
  LockStatus = "consultNoteDiagnosis:lock:status",
  DraftAck = "consultNoteDiagnosis:draft:ack",
  DraftLoad = "consultNoteDiagnosis:draft:load",
  UnlockStatus = "consultNoteDiagnosis:unlock:status",
  UnlockError = "consultNoteDiagnosis:unlock:error",
  JoinRoomError = "consultNoteDiagnosis:join:error",
  DraftError = "consultNoteDiagnosis:draft:error",
  KeepAliveError = "consultNoteDiagnosis:keepalive:error",
}

// ==================== Letters/MCs Events ====================

export enum LetterMcsClientEvents {
  JoinRoom = "letterMcs:joinRoom",
  // LeaveRoom removed - server no longer supports it
  RequestLock = "letterMcs:lock",
  KeepAlive = "letterMcs:keepalive",
  DraftUpdate = "letterMcs:draft:update",
  ReleaseLock = "letterMcs:unlock",
}

export enum LetterMcsServerEvents {
  Snapshot = "letterMcs:lock:snapshot",
  LockAck = "letterMcs:lock:ack",
  LockError = "letterMcs:lock:error",
  LockStatus = "letterMcs:lock:status",
  DraftAck = "letterMcs:draft:ack",
  DraftLoad = "letterMcs:draft:load",
  UnlockStatus = "letterMcs:unlock:status",
  UnlockError = "letterMcs:unlock:error",
  JoinRoomError = "letterMcs:join:error",
  DraftError = "letterMcs:draft:error",
  KeepAliveError = "letterMcs:keepalive:error",
}

// ==================== Nursing Note Events ====================

export enum NursingNoteClientEvents {
  JoinRoom = "nursingNote:joinRoom",
  // LeaveRoom removed - server no longer supports it
  RequestLock = "nursingNote:lock",
  KeepAlive = "nursingNote:keepalive",
  DraftUpdate = "nursingNote:draft:update",
  ReleaseLock = "nursingNote:unlock",
}

export enum NursingNoteServerEvents {
  Snapshot = "nursingNote:lock:snapshot",
  LockAck = "nursingNote:lock:ack",
  LockError = "nursingNote:lock:error",
  LockStatus = "nursingNote:lock:status",
  DraftAck = "nursingNote:draft:ack",
  DraftLoad = "nursingNote:draft:load",
  UnlockStatus = "nursingNote:unlock:status",
  UnlockError = "nursingNote:unlock:error",
  JoinRoomError = "nursingNote:join:error",
  DraftError = "nursingNote:draft:error",
  KeepAliveError = "nursingNote:keepalive:error",
}

// ==================== Dispensing & Charting Events ====================

export enum DispensingClientEvents {
  JoinRoom = "dispensing:joinRoom",
  // LeaveRoom removed - server no longer supports it
  RequestLock = "dispensing:lock",
  KeepAlive = "dispensing:keepalive",
  DraftUpdate = "dispensing:draft:update",
  ReleaseLock = "dispensing:unlock",
}

export enum DispensingServerEvents {
  Snapshot = "dispensing:lock:snapshot",
  LockAck = "dispensing:lock:ack",
  LockError = "dispensing:lock:error",
  LockStatus = "dispensing:lock:status",
  DraftAck = "dispensing:draft:ack",
  DraftLoad = "dispensing:draft:load",
  UnlockStatus = "dispensing:unlock:status",
  UnlockError = "dispensing:unlock:error",
  JoinRoomError = "dispensing:join:error",
  DraftError = "dispensing:draft:error",
  KeepAliveError = "dispensing:keepalive:error",
}

// ==================== Diagnosis Events ====================

export enum DiagnosisClientEvents {
  JoinRoom = "diagnosis:joinRoom",
  RequestLock = "diagnosis:lock",
  KeepAlive = "diagnosis:keepalive",
  DraftUpdate = "diagnosis:draft:update",
  ReleaseLock = "diagnosis:unlock",
}

export enum DiagnosisServerEvents {
  Snapshot = "diagnosis:lock:snapshot",
  LockAck = "diagnosis:lock:ack",
  LockError = "diagnosis:lock:error",
  LockStatus = "diagnosis:lock:status",
  DraftAck = "diagnosis:draft:ack",
  DraftLoad = "diagnosis:draft:load",
  UnlockStatus = "diagnosis:unlock:status",
  UnlockError = "diagnosis:unlock:error",
  JoinRoomError = "diagnosis:join:error",
  DraftError = "diagnosis:draft:error",
  KeepAliveError = "diagnosis:keepalive:error",
}

// ==================== Patient Disposition Events ====================

export enum PatientDispositionClientEvents {
  JoinRoom = "patientDisposition:joinRoom",
  RequestLock = "patientDisposition:lock",
  KeepAlive = "patientDisposition:keepalive",
  DraftUpdate = "patientDisposition:draft:update",
  ReleaseLock = "patientDisposition:unlock",
}

export enum PatientDispositionServerEvents {
  Snapshot = "patientDisposition:lock:snapshot",
  LockAck = "patientDisposition:lock:ack",
  LockError = "patientDisposition:lock:error",
  LockStatus = "patientDisposition:lock:status",
  DraftAck = "patientDisposition:draft:ack",
  DraftLoad = "patientDisposition:draft:load",
  UnlockStatus = "patientDisposition:unlock:status",
  UnlockError = "patientDisposition:unlock:error",
  JoinRoomError = "patientDisposition:join:error",
  DraftError = "patientDisposition:draft:error",
  KeepAliveError = "patientDisposition:keepalive:error",
}

// ==================== Discharge Advice Events ====================

export enum DischargeAdviceClientEvents {
  JoinRoom = "dischargeAdvice:joinRoom",
  RequestLock = "dischargeAdvice:lock",
  KeepAlive = "dischargeAdvice:keepalive",
  DraftUpdate = "dischargeAdvice:draft:update",
  ReleaseLock = "dischargeAdvice:unlock",
}

export enum DischargeAdviceServerEvents {
  Snapshot = "dischargeAdvice:lock:snapshot",
  LockAck = "dischargeAdvice:lock:ack",
  LockError = "dischargeAdvice:lock:error",
  LockStatus = "dischargeAdvice:lock:status",
  DraftAck = "dischargeAdvice:draft:ack",
  DraftLoad = "dischargeAdvice:draft:load",
  UnlockStatus = "dischargeAdvice:unlock:status",
  UnlockError = "dischargeAdvice:unlock:error",
  JoinRoomError = "dischargeAdvice:join:error",
  DraftError = "dischargeAdvice:draft:error",
  KeepAliveError = "dischargeAdvice:keepalive:error",
}

// ==================== Consultation Lock Events ====================

export enum ConsultationLockClientEvents {
  JoinRoom = "joinRoom",
  // LeaveRoom removed - server no longer supports it
}

export enum ConsultationLockServerEvents {
  LockSnapshot = "snapshot",
  LockUpdate = "update",
  JoinRoomError = "joinRoomError",
  // LeaveRoomError removed - server no longer supports it
}

// Individual section lock detail from server
export interface SectionLockDetail {
  /** Room name for this section */
  roomName: string;

  /** User ID of the person who locked this section */
  lockedBy: string;

  /** Lock timestamp (Unix milliseconds) */
  lockedAt: number;

  /** Username of the person who locked this section */
  lockedByUsername: string;
}

// Consultation lock snapshot containing all sections' lock states
// This matches the actual server response format
export interface ConsultationLockSnapshot {
  /** Last update timestamp (ISO string) */
  updatedAt: string;

  /** Name of user who made the last update */
  updaterName: string;

  /** Whether the consultation is published */
  isConsultationPublished: boolean;

  /** Consultation note/diagnosis lock state (null if not locked) */
  consultaNoteLockSnapshot: SectionLockDetail | null;

  /** Dispensing lock state (null if not locked) */
  dispensingLockSnapshot: SectionLockDetail | null;

  /** Letter/MC lock state (null if not locked) */
  letterMcsLockSnapshot: SectionLockDetail | null;

  /** Nursing note lock state (null if not locked) */
  nursingNoteLockSnapshot: SectionLockDetail | null;

  /** Patient disposition lock state (null if not locked) */
  patientDispositionLockSnapshot?: SectionLockDetail | null;

  /** Discharge advice lock state (null if not locked) */
  dischargeAdviceLockSnapshot?: SectionLockDetail | null;
}

// Legacy type for backward compatibility (deprecated, use ConsultationLockSnapshot directly)
export interface SectionLockState {
  /** Whether this section is currently locked */
  isLocked: boolean;

  /** User ID of the person who locked this section */
  lockedBy: string | null;

  /** Username of the person who locked this section */
  lockedByUsername?: string;

  /** Lock time */
  lockedAt?: string | Date;
}

// ==================== Session Events ====================

export enum SessionClientEvents {
  RegisterSession = "register_session",
  KeepAlive = "session:keepalive",
}

export enum SessionServerEvents {
  // Names must match exactly what the auth-service emits (MS32 gateway.ts).
  SessionRegistered = "register_session:ack",
  SessionExpiring = "session_expiring",
  ForceLogout = "force_logout",
  SessionError = "register_session:error",
}

// Session data structure
export interface SessionData {
  /** Unique session identifier */
  sessionId: string;

  /** Session expiration time in seconds */
  expiresIn: number;
}

// Session expiring warning data
export interface SessionExpiringData {
  /** Session ID that is expiring */
  sessionId: string;

  /** Seconds remaining until force logout */
  remainingSeconds: number;

  /** Message for display */
  message?: string;
}

// Force logout data
export interface ForceLogoutData {
  /** Session ID that was logged out */
  sessionId: string;

  /** Reason for logout */
  reason: string;

  /** Message for display */
  message?: string;
}

// ==================== Event Mapping Types ====================

export interface CollaborativeEvents {
  consultNoteDiagnosis: {
    client: ConsultNoteDiagnosisClientEvents;
    server: ConsultNoteDiagnosisServerEvents;
  };
  letterMcs: {
    client: LetterMcsClientEvents;
    server: LetterMcsServerEvents;
  };
  nursingNote: {
    client: NursingNoteClientEvents;
    server: NursingNoteServerEvents;
  };
  dispensing: {
    client: DispensingClientEvents;
    server: DispensingServerEvents;
  };
  diagnosis: {
    client: DiagnosisClientEvents;
    server: DiagnosisServerEvents;
  };
  patientDisposition: {
    client: PatientDispositionClientEvents;
    server: PatientDispositionServerEvents;
  };
  consultationLock: {
    client: ConsultationLockClientEvents;
    server: ConsultationLockServerEvents;
  };
  session: {
    client: SessionClientEvents;
    server: SessionServerEvents;
  };
}

// ==================== Hook Configuration Types ====================

export interface CollaborativeLockConfig {
  module: CollaborativeModule;
  accessId: string;
  consultationId: string;
  autoSaveEnabled?: boolean;
  autoSaveInterval?: number; // Default 30 seconds
  onLockAcquired?: (snapshot: LockSnapshot) => void;
  onLockReleased?: () => void;
  onDraftSaved?: (timestamp: Date) => void;
  onError?: (error: string) => void;
}

export interface CollaborativeLockActions {
  requestLock: () => void;
  releaseLock: () => void;
  updateDraft: (payload?: DraftUpdatePayload) => void;
  keepAlive: () => void;
  joinRoom: () => void;
  isEditable: boolean;
  isLocked: boolean;
  lockSnapshot: LockSnapshot | null;
  lastSaved: Date | null;
}
